import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, ExternalLink, Play, Share2, Train, Video } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const TrilhosTrem = () => {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const videos = [
    {
      titulo: "Tudo entreguei ao senhor",
      link: "https://www.youtube.com/watch?v=9nfiZe2-f0c",
      thumbnail: "https://i.ytimg.com/vi/9nfiZe2-f0c/hqdefault.jpg",
      descricao: "Música de louvor e adoração - Tudo entreguei ao senhor."
    },
    {
      titulo: "Viagem de trem - São Paulo a Santos",
      link: "https://www.youtube.com/watch?v=9nfiZe2-f0c",
      thumbnail: "https://i.ytimg.com/vi/9nfiZe2-f0c/hqdefault.jpg",
      descricao: "Conheça a histórica viagem de trem de São Paulo a Santos pela Serra do Mar."
    },
    {
      titulo: "Trens do Brasil - Documentário",
      link: "https://www.youtube.com/watch?v=9nfiZe2-f0c",
      thumbnail: "https://i.ytimg.com/vi/9nfiZe2-f0c/hqdefault.jpg",
      descricao: "Documentário sobre a história dos trens no Brasil."
    }
  ];

  const playlists = [
    {
      nome: "Trens do Brasil",
      link: "https://www.youtube.com/playlist?list=PLFZOAa2QxRCmDvkY_BpZcFLOoNRMbnnBK",
      descricao: "Conheça os trens e ferrovias brasileiras"
    },
    {
      nome: "Trens do Mundo",
      link: "https://www.youtube.com/playlist?list=PLFZOAa2QxRCmDvkY_BpZcFLOoNRMbnnBK",
      descricao: "As mais belas ferrovias e trens ao redor do mundo"
    },
    {
      nome: "História Ferroviária",
      link: "https://www.youtube.com/playlist?list=PLFZOAa2QxRCmDvkY_BpZcFLOoNRMbnnBK",
      descricao: "Documentários sobre a história das ferrovias"
    },
    {
      nome: "Modelismo Ferroviário",
      link: "https://www.youtube.com/playlist?list=PLFZOAa2QxRCmDvkY_BpZcFLOoNRMbnnBK",
      descricao: "Miniaturas e maquetes de trens"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Canal Trilhos e Trem
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Sua viagem pelo fascinante mundo dos trens e ferrovias começa aqui.
          </p>
        </div>

        <div className="mb-12">
          <Card className="overflow-hidden border border-border">
            <CardHeader className="bg-muted/50">
              <CardTitle className="text-xl flex items-center">
                <Train className="mr-2 h-5 w-5 text-primary" /> Trilhos e Trem no YouTube
              </CardTitle>
              <CardDescription>
                Conheça nosso canal dedicado ao universo ferroviário
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  Nosso canal no YouTube traz conteúdos sobre trens, ferrovias, viagens de trem, curiosidades e histórias relacionadas ao mundo ferroviário. Inscreva-se e acompanhe nossas atualizações.
                </p>
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <Button 
                    asChild 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href="https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Video className="mr-2 h-5 w-5" /> Visitar Canal no YouTube
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => handleCopy("https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ")}
                    className={copied === "https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" ? "bg-primary/10" : ""}
                  >
                    <Copy size={16} className="mr-1" /> 
                    {copied === "https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" ? "Copiado!" : "Copiar Link"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Vídeo em Destaque</h2>
          <Card className="overflow-hidden border border-border">
            <div className="aspect-video overflow-hidden bg-muted">
              <img 
                src="https://i.ytimg.com/vi/9nfiZe2-f0c/maxresdefault.jpg" 
                alt="Tudo entreguei ao senhor"
                className="w-full h-full object-cover"
              />
            </div>
            <CardHeader>
              <CardTitle>Tudo entreguei ao senhor</CardTitle>
              <CardDescription>
                Uma bela música de louvor e adoração para elevar sua alma.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Este vídeo apresenta a música "Tudo entreguei ao senhor", uma canção que inspira a entrega total a Deus. Com uma melodia tocante e letra profunda, esta música tem tocado o coração de milhares de pessoas.
              </p>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a 
                  href="https://www.youtube.com/watch?v=9nfiZe2-f0c" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Play size={16} className="mr-1" /> Assistir Agora
                </a>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleCopy("https://www.youtube.com/watch?v=9nfiZe2-f0c")}
                className={copied === "https://www.youtube.com/watch?v=9nfiZe2-f0c" ? "bg-primary/10" : ""}
              >
                <Share2 size={16} className="mr-1" /> 
                {copied === "https://www.youtube.com/watch?v=9nfiZe2-f0c" ? "Copiado!" : "Compartilhar"}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Nossos Vídeos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card key={index} className="overflow-hidden border border-border h-full flex flex-col">
                <div className="aspect-video overflow-hidden bg-muted">
                  <img 
                    src={video.thumbnail} 
                    alt={video.titulo}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{video.titulo}</CardTitle>
                  <CardDescription>{video.descricao}</CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-between mt-auto">
                  <Button 
                    asChild 
                    variant="default"
                    size="sm"
                  >
                    <a 
                      href={video.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                    >
                      <Play size={16} className="mr-1" /> Assistir
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCopy(video.link)}
                    className={copied === video.link ? "bg-primary/10" : ""}
                  >
                    <Share2 size={16} className="mr-1" /> 
                    {copied === video.link ? "Copiado!" : "Compartilhar"}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Nossas Playlists</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {playlists.map((playlist, index) => (
              <Card key={index} className="flex flex-col h-full">
                <CardHeader>
                  <CardTitle>{playlist.nome}</CardTitle>
                  <CardDescription>{playlist.descricao}</CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground">
                    Uma coleção cuidadosamente selecionada de vídeos sobre {playlist.nome.toLowerCase()}.
                  </p>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a 
                      href={playlist.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Ver Playlist
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-muted p-6 rounded-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4">Sobre o Canal</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Nossa Missão</h3>
              <p className="text-sm text-muted-foreground">Compartilhar o amor pelas ferrovias e trens, preservando sua história e promovendo seu futuro.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Nosso Conteúdo</h3>
              <p className="text-sm text-muted-foreground">Documentários, viagens, curiosidades, história ferroviária e muito mais para os amantes de trens.</p>
            </div>
            <div className="bg-card p-4 rounded-lg border border-border">
              <h3 className="text-lg font-medium mb-2">Participe</h3>
              <p className="text-sm text-muted-foreground">Envie suas sugestões, compartilhe suas experiências e faça parte da nossa comunidade ferroviária.</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://studio.youtube.com/channel/UCjRjVRJpkKHIrtnBPFS2bfQ" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Inscreva-se no Canal
            </a>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TrilhosTrem;